import React, { useState } from "react";
import { X, Save, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

import { ImageUploadField } from "@/pages/admin/fields/ImageUploadField";
import { Section, TextField, IconBtn } from "@/pages/admin/fields/primitives";

/**
 * UseCaseEditorDrawer — Workstations Final System Pass (Feb 2026)
 *
 * Edits a single `use_cases` document via PUT /api/admin/use-cases/{slug}.
 * Owner-facing labels are customer-friendly, but backend field names are
 * preserved 1:1 so we never fork the schema.
 *
 * Read-only fields:
 *   • Slug (used in /workstations/[slug] URL — never editable to avoid
 *     breaking historical links / orders)
 *   • Default Hardware Class (this mapping controls core product
 *     architecture + pricing behavior — protected from accidental change
 *     during normal merchandising, per owner directive)
 */
export const UseCaseEditorDrawer = ({ item, softwareOptions = [], onClose, onSave }) => {
  const [form, setForm] = useState(() => ({
    slug: "",
    display_name: "",
    byline: "",
    short_description: "",
    long_description: "",
    image: "",
    image_alt: "",
    icon_url: "",
    features: [],
    recommended_software: [],
    default_hardware_class: "",
    windows_supported: true,
    mac_supported: false,
    platform_caveat: "",
    active: true,
    display_order: 0,
    ...(item || {}),
  }));

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    const numeric = k === "display_order";
    setForm((f) => ({ ...f, [k]: numeric ? Number(v || 0) : v }));
  };
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  // ---- Features (list of strings) ----
  const addFeature = () => setForm((f) => ({ ...f, features: [...(f.features || []), ""] }));
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, j) => j !== i) }));
  const setFeature = (i, v) => setForm((f) => ({
    ...f,
    features: f.features.map((x, j) => (j === i ? v : x)),
  }));

  // ---- Recommended software (list of {name, power_slug, note}) ----
  const addRec = () => setForm((f) => ({
    ...f,
    recommended_software: [...(f.recommended_software || []), { name: "", power_slug: "", note: "" }],
  }));
  const removeRec = (i) => setForm((f) => ({
    ...f,
    recommended_software: f.recommended_software.filter((_, j) => j !== i),
  }));
  const setRec = (i, patch) => setForm((f) => ({
    ...f,
    recommended_software: f.recommended_software.map((x, j) => (j === i ? { ...x, ...patch } : x)),
  }));

  const commit = () => {
    if (!form.display_name?.trim()) {
      toast.error("Display Name is required.");
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-end" onClick={onClose}>
      <aside
        data-testid="use-case-editor-drawer"
        className="bg-[#0F0F0F] w-full max-w-2xl h-full border-l border-[#262626] p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6 sticky top-0 -mt-6 -mx-6 px-6 py-4 bg-[#0F0F0F] border-b border-[#262626] z-10">
          <div>
            <div className="ur-eyebrow">Workstation Product</div>
            <h3 className="font-display text-xl font-bold mt-1">
              Edit · {form.display_name || form.slug}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="use-case-save"
              onClick={commit}
              className="ur-btn-primary text-sm py-2.5 px-4"
            >
              Save <Save className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Basics */}
        <Section title="Basics" destination="Renders on: Home teaser card + /workstations grid + /workstations/[slug] hero">
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Slug (read-only)" value={form.slug} onChange={() => {}} hint="Used in URL: /workstations/{slug}" />
            <TextField label="Display Name" value={form.display_name} onChange={set("display_name")} placeholder="Content & Marketing" />
            <TextField label="Byline (Home card)" value={form.byline} onChange={set("byline")} placeholder="Ready-to-work setup for…" />
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Display Order</label>
              <input
                type="number"
                value={form.display_order}
                onChange={set("display_order")}
                className="mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
              />
            </div>
          </div>
          <TextField
            label="Short Description"
            textarea
            rows={2}
            value={form.short_description}
            onChange={set("short_description")}
            placeholder="One sentence used on /workstations index card and product hero."
          />
          <TextField
            label="Long Description"
            textarea
            rows={4}
            value={form.long_description}
            onChange={set("long_description")}
            placeholder="Reserved for future expanded copy. Not required."
          />
        </Section>

        {/* Product image */}
        <Section
          title="Product Image"
          destination="Renders on: Home card + /workstations card + /workstations/[slug] hero"
          hint="Single source of truth — the same image is used in all three surfaces. Upload replaces the previous image; there is no versioning."
        >
          <ImageUploadField
            value={form.image}
            onChange={(url) => setForm((f) => ({ ...f, image: url }))}
          />
        </Section>

        {/* Hover image (optional) — Workstation Shift experiment */}
        <Section
          title="Hover Image (Optional)"
          destination="Shown on hover: Home workstation cards (supported desktop pointer devices)"
          hint="Optional alternate artwork shown when hovering over this workstation card on supported desktop devices."
        >
          <ImageUploadField
            value={form.image_alt || ""}
            onChange={(url) => setForm((f) => ({ ...f, image_alt: url }))}
          />
        </Section>

        {/* Features */}
        <Section title="Features / Benefits" hint="Bullet list rendered on /workstations index cards.">
          <div className="space-y-2" data-testid="use-case-features-editor">
            {(form.features || []).map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={line}
                  onChange={(e) => setFeature(i, e.target.value)}
                  className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
                <IconBtn onClick={() => removeFeature(i)} danger><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addFeature}
            data-testid="use-case-add-feature"
            className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF6600] font-semibold"
          >
            <Plus className="w-4 h-4" /> Add Feature
          </button>
        </Section>

        {/* Recommended software */}
        <Section
          title="Recommended Software"
          hint="Products with a matching Software Catalog entry render as 'Available from Ur Designs' with a VIEW SOFTWARE CTA. Leave Software Catalog blank for compatible-but-not-sold recommendations."
        >
          <div className="space-y-3" data-testid="use-case-recommended-editor">
            {(form.recommended_software || []).map((rs, i) => (
              <div key={i} className="rounded-xl border border-[#262626] bg-[#141414] p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Software Name"
                    value={rs.name || ""}
                    onChange={(e) => setRec(i, { name: e.target.value })}
                    className="flex-1 bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                  />
                  <IconBtn onClick={() => removeRec(i)} danger><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
                <select
                  value={rs.power_slug || ""}
                  onChange={(e) => setRec(i, { power_slug: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                >
                  <option value="">— None (recommendation only) —</option>
                  {softwareOptions.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name} ({s.slug})</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Note (optional — appears as sub-text on the software chip)"
                  value={rs.note || ""}
                  onChange={(e) => setRec(i, { note: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addRec}
            data-testid="use-case-add-recommended"
            className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF6600] font-semibold"
          >
            <Plus className="w-4 h-4" /> Add Recommendation
          </button>
        </Section>

        {/* Platform + mapping */}
        <Section title="Platform Availability" hint="Mac-supported use cases show the Windows/Mac picker on the product page.">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-3 rounded-xl border border-[#262626] bg-[#141414] p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.windows_supported}
                onChange={toggle("windows_supported")}
                className="accent-[#FF6600]"
                data-testid="use-case-toggle-windows"
              />
              <div>
                <div className="text-sm font-semibold">Windows Supported</div>
                <div className="text-[11px] text-[#A3A3A3]">Show Windows option on the product page.</div>
              </div>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-[#262626] bg-[#141414] p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.mac_supported}
                onChange={toggle("mac_supported")}
                className="accent-[#FF6600]"
                data-testid="use-case-toggle-mac"
              />
              <div>
                <div className="text-sm font-semibold">Mac Supported</div>
                <div className="text-[11px] text-[#A3A3A3]">Show Mac option on the product page.</div>
              </div>
            </label>
          </div>
          <TextField
            label="Platform Caveat (optional)"
            value={form.platform_caveat}
            onChange={set("platform_caveat")}
            placeholder="e.g. Autodesk workflows recommend Windows for optimal compatibility."
          />

          <div className="mt-4 rounded-xl border border-[#262626] bg-[#0A0A0A] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Hardware Class</div>
            <div
              data-testid="use-case-hardware-class-readonly"
              className="mt-1 text-[15px] font-semibold text-white capitalize"
            >
              {form.default_hardware_class || "—"}
            </div>
            <div className="text-[11px] text-[#7A7A7A] mt-1 italic">
              System mapping — not editable here. Changes to this mapping affect pricing behavior and must be made outside normal merchandising.
            </div>
          </div>
        </Section>

        {/* Visibility */}
        <Section title="Visibility">
          <label className="flex items-center gap-3 rounded-xl border border-[#262626] bg-[#141414] p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.active}
              onChange={toggle("active")}
              className="accent-[#FF6600]"
              data-testid="use-case-toggle-active"
            />
            <div>
              <div className="text-sm font-semibold">Active</div>
              <div className="text-[11px] text-[#A3A3A3]">Inactive workstations don't appear on the customer site.</div>
            </div>
          </label>
        </Section>
      </aside>
    </div>
  );
};
